import {
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlPagination,
} from "@monbits/ui";
import { Coins, Layers } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import { TypeBadge } from "src/components/type-badge";
import transactionProvider, {
	type PaginatedTransactionsResponse,
	type TransactionDetails,
} from "src/providers/transaction-provider";
import {
	chainShortOptions as BLOCKCHAIN_OPTIONS,
	cryptoTickerOptions as CURRENCY_OPTIONS,
} from "src/utils/options";
import {
	getStatusConfig as getUnifiedStatusConfig,
	STATUS_CONFIG,
	TRANSACTION_TYPE_CONFIG,
} from "src/utils/status-config";

// Transaction status values to show in filter
const TX_STATUSES = [
	"pending",
	"inProgress",
	"completed",
	"failed",
	"canceled",
] as const;

// Generate status options from unified config
const TX_STATUS_OPTIONS = TX_STATUSES.map((status) => {
	const config = STATUS_CONFIG[status];
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

// Transaction type values to show in filter
const TX_TYPES = [
	"incoming",
	"internal",
	"withdrawal",
	"pool_withdrawal",
	"fund_return",
] as const;

// Generate type options from unified config
const TX_TYPE_OPTIONS = TX_TYPES.map((type) => {
	const config = TRANSACTION_TYPE_CONFIG[type];
	return {
		value: type,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <STATUS_CONFIG.pending.icon className="h-4 w-4" />,
		options: TX_STATUS_OPTIONS,
	},
	{
		id: "type",
		label: "Type",
		type: "select",
		icon: <TRANSACTION_TYPE_CONFIG.incoming.icon className="h-4 w-4" />,
		options: TX_TYPE_OPTIONS,
	},
	{
		id: "currency",
		label: "Currency",
		type: "select",
		icon: <Coins className="h-4 w-4" />,
		options: CURRENCY_OPTIONS,
	},
	{
		id: "blockchain",
		label: "Blockchain",
		type: "select",
		icon: <Layers className="h-4 w-4" />,
		options: BLOCKCHAIN_OPTIONS,
	},
];

interface TransactionsListProps {
	transactions: TransactionDetails[];
	totalCount: number;
	isLoading: boolean;
	setTransactions: React.Dispatch<React.SetStateAction<TransactionDetails[]>>;
	setTotalCount: React.Dispatch<React.SetStateAction<number>>;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) return dateStr;
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const formatAmount = (value?: string | null): string => {
	if (!value) return "0";
	return value;
};

// Use unified status config
const getStatusConfig = (status: string) => {
	// Handle some special case mappings
	const normalized = status?.toLowerCase?.() ?? "";
	let lookupKey = normalized;

	// Map alternative names to canonical names
	if (normalized === "completedinv") lookupKey = "completed";
	if (normalized === "inprogressinv") lookupKey = "inProgress";
	if (normalized === "onhold") lookupKey = "on_hold";
	if (normalized === "inprogress") lookupKey = "inProgress";

	const config = getUnifiedStatusConfig(lookupKey);
	return {
		type: config.badgeType,
		icon: config.icon,
		label: config.label,
	};
};

const getTypeBadgeType = (
	type: string,
): "incoming" | "internal" | "withdrawal" | "fund_return" | "default" => {
	switch (type?.toLowerCase?.()) {
		case "incoming":
			return "incoming";
		case "internal":
			return "internal";
		case "withdrawal":
		case "pool_withdrawal":
			return "withdrawal";
		case "fund_return":
			return "fund_return";
		default:
			return "default";
	}
};

const formatTransactionType = (type: string): string => {
	switch (type?.toLowerCase?.()) {
		case "incoming":
			return "Incoming";
		case "internal":
			return "Internal";
		case "withdrawal":
			return "Withdrawal";
		case "pool_withdrawal":
			return "Pool Withdrawal";
		case "fund_return":
			return "Fund Return";
		default:
			return type;
	}
};

const columns: ColumnDef<TransactionDetails>[] = [
	{
		id: "merchant",
		header: "Merchant",
		cell: (row) => (
			<>
				<span className="font-medium">{row.merchantName}</span>
				{row.isTest && (
					<StatusBadge
						type="info"
						className="ml-2 text-[10px] uppercase tracking-wide"
					>
						Test
					</StatusBadge>
				)}
			</>
		),
	},
	{
		id: "type",
		header: "Type",
		cell: (row) => (
			<TypeBadge type={getTypeBadgeType(row.type)}>
				{formatTransactionType(row.type)}
			</TypeBadge>
		),
	},
	{
		id: "status",
		header: "Status",
		cell: (row) => {
			const config = getStatusConfig(row.status);
			return (
				<StatusBadge type={config.type} icon={config.icon}>
					{config.label}
				</StatusBadge>
			);
		},
	},
	{
		id: "currency",
		header: "Currency",
		cell: (row) => (
			<CryptoIcon
				ticker={row.currency}
				showLabel
				showComposite
				className="h-5 w-5"
			/>
		),
	},
	{
		id: "amount",
		header: "Amount",
		className: "text-right",
		cell: (row) => (
			<span className="font-mono">{formatAmount(row.amount)}</span>
		),
	},
	{
		id: "usd",
		header: "USD",
		className: "text-right",
		cell: (row) => (
			<span className="font-mono">${formatAmount(row.usdAmount)}</span>
		),
	},
	{
		id: "created",
		header: "Created",
		cell: (row) => formatDate(row.createdAt),
	},
];

const TransactionsList: React.FC<TransactionsListProps> = ({
	transactions,
	totalCount,
	isLoading,
	setTransactions,
	setTotalCount,
	setIsLoading,
}) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const search = searchParams.get("search") || "";

	const { page, limit, offset, setPage, setLimit } = useUrlPagination({
		totalCount,
	});

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			status?: string;
			type?: string;
			currency?: string;
			blockchain?: string;
		} = {};

		for (const f of filters) {
			if (f.operator === "is") {
				if (f.field === "status") {
					result.status = f.value;
				} else if (f.field === "type") {
					result.type = f.value;
				} else if (f.field === "currency") {
					result.currency = f.value;
				} else if (f.field === "blockchain") {
					result.blockchain = f.value;
				}
			}
		}

		return result;
	}, [filters]);

	const loadTransactions = React.useCallback(async () => {
		try {
			setIsLoading(true);
			const response: PaginatedTransactionsResponse =
				await transactionProvider.listTransactions({
					limit,
					offset,
					search,
					status: apiFilters.status,
					type: apiFilters.type,
					currency: apiFilters.currency,
					blockchain: apiFilters.blockchain,
				});

			setTransactions(response.transactions);
			setTotalCount(response.totalCount);
		} catch (error) {
			console.error("Failed to load transactions:", error);
			toast.error("Failed to load transactions", {
				description: "Please try again later",
			});
		} finally {
			setIsLoading(false);
		}
	}, [
		limit,
		offset,
		search,
		apiFilters,
		setIsLoading,
		setTotalCount,
		setTransactions,
	]);

	React.useEffect(() => {
		loadTransactions();
	}, [loadTransactions]);

	const handleViewTransaction = React.useCallback(
		(transaction: TransactionDetails) => {
			const queryString = searchParams.toString();
			const targetPath = queryString
				? `/transactions/${transaction.id}?${queryString}`
				: `/transactions/${transaction.id}`;
			navigate(targetPath);
		},
		[navigate, searchParams],
	);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">Transaction Management</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{totalCount} total transaction{totalCount !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			<div className="flex gap-4 flex-wrap items-center mb-4">
				<SearchInput
					placeholder="Search by email, merchant, currency, or hash..."
					paramName="search"
					className="w-[350px]"
				/>
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={transactions}
				isLoading={isLoading}
				onRowClick={handleViewTransaction}
				getRowId={(row) => String(row.id)}
				emptyMessage={
					search || filters.length > 0
						? "No transactions found. Try adjusting your search or filter criteria."
						: "No transactions yet. Transactions will appear here once they occur."
				}
				pagination={{
					page,
					pageSize: limit,
					totalCount,
					onPageChange: setPage,
					onPageSizeChange: setLimit,
				}}
			/>
		</div>
	);
};

export default TransactionsList;
